import { ElementRef, Injectable } from "@angular/core";
import {
  HsEventBusService,
  HsLanguageService,
  HsLayoutService,
  HsLayerManagerService,
  HsQueryBaseService,
  HsUtilsService,
} from "hslayers-ng";
import { ChartsService } from "../charts/charts.service";
import { chartsData } from "../charts/charts-data.interface";
import cropLegendLabels from "./data/crop-legend-labels.json";
import cropModelsJson from "./data/cropModels.json";
import { StrategyCommonService } from "./strategy-common.service";
import { StrategyDataParserService } from "./strategy-data-parser.service";
import { HttpClient } from "@angular/common/http";
import { forkJoin, Subject } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class StrategyBaseChartsService {
  dialogElement: ElementRef;
  datasets;
  definitions;

  dataFrequency;

  chartData: any;
  strategyChartData;
  zoom = false;

  //Agro climatic API
  fetchedData = [];
  selectedDate;

  cropModels;
  cropLegend;
  preciLegends = ["offseason", "necessary", "reserve", "excess"];

  createBaseCharts = new Subject();
  constructor(
    public HsEventBusService: HsEventBusService,
    public hsLangService: HsLanguageService,
    public ChartsService: ChartsService,
    public HsLayoutService: HsLayoutService,
    public HsLayerManagerService: HsLayerManagerService,
    public HsQueryBaseService: HsQueryBaseService,
    private http: HttpClient,
    public HsUtilsService: HsUtilsService,
    public StrategyCommonService: StrategyCommonService,
    public parserService: StrategyDataParserService
  ) {
    this.cropModels = { ...cropModelsJson };
    this.cropLegend = { ...cropLegendLabels };

    //Adding 'before' stage representing dates before the season begins
    for (const [key, crop] of Object.entries(this.cropLegend)) {
      if (crop[0] !== "unsufficient") {
        (crop as any).unshift("offseason");
      }
    }
    this.StrategyCommonService.fetchData$.subscribe((coords) => {
      this.fetchClimaticData(coords);
    });
    this.StrategyCommonService.renderCharts$.subscribe((coords) => {
      this.renderCharts();
    });
  }

  renderCharts() {
    this.strategyChartData = <chartsData>{
      datasets: this.datasets,
      definitions: this.getDefinitions(),
      dataFrequency: this.dataFrequency,
      uncertainity: this.StrategyCommonService.uncertainity,
      componentChartOverrides: this.strategyChartOverrides,
      chartContainerClass: ".ac-chartplace",
      columns: 1,
      overview: this.StrategyCommonService.overview,
      chartHeight: 0.18,
    };
  }

  private getHsuCropParam() {
    switch (this.StrategyCommonService.selectedCrop) {
      case "corn":
        return "3";
      case "wheat":
        return "1";
      default:
        return "0";
    }
  }

  fetchClimaticData(coords) {
    // Consider cache
    this.dataFrequency = this.StrategyCommonService.dataFrequency;
    this.selectedDate = this.StrategyCommonService.selectedDate;
    this.StrategyCommonService.baseChartsLoading = true;
    this.fetchedData = [];

    const observables = [];
    const cropSelected =
      this.StrategyCommonService.overview == "crop"
        ? this.StrategyCommonService.selectedCrop == "corn"
          ? "3"
          : "2"
        : "0";
    const urlAppend =
      this.dataFrequency === "week" ? "1/53/ERA5-Land" : "ERA5-Land";

    let endDate, startDate;

    endDate =
      this.dataFrequency == "week"
        ? this.selectedDate.upper + 1
        : this.selectedDate.upper;
    startDate =
      this.dataFrequency == "week"
        ? this.selectedDate.upper
        : this.selectedDate.lower;

    //Create dateRange Array<any>
    const dateRange = this.createDateRangeObject(
      this.dataFrequency,
      startDate,
      endDate
    );

    const factors =
      this.StrategyCommonService.selectedDataPairs[
        this.StrategyCommonService.overview
      ][this.dataFrequency];

    //Add ffdate factor if its not already in list to get season end date
    if (
      this.dataFrequency == "week" &&
      !factors.some((factor) => factor.data == "ffdate")
    ) {
      ["ffdate", "lfdate"].forEach((factor) => {
        const url = [
          this.StrategyCommonService.apiUrl,
          "year",
          factor,
          cropSelected,
          coords[1],
          coords[0],
          "0.1",
          (startDate - 1).toString(), //Need 3 year to calculate frost season..maybe even 4
          endDate.toString(),
          "ERA5-Land",
        ].join("/");

        const promise = this.StrategyCommonService.makeClimaticDataRequest(
          this.HsUtilsService.proxify(url),
          factor,
          this.fetchedData
        );
        observables.push(promise);
      });
    }
    //TODO: loop through all factors wanted
    for (const factor of factors) {
      if (Array.isArray(factor.data)) {
        for (const [index, subfactor] of factor.data.entries()) {
          if (!this.fetchedData[subfactor]) {
            const url = this.getFactorRequestUrl(
              subfactor,
              factor.mark[index],
              subfactor === "hsu" ? this.getHsuCropParam() : cropSelected,
              coords,
              startDate,
              endDate,
              urlAppend
            );
            const promise = this.StrategyCommonService.makeClimaticDataRequest(
              this.HsUtilsService.proxify(url),
              subfactor,
              this.fetchedData
            );
            observables.push(promise);
          }
        }
      } else {
        const factorName =
          factor.mark == "rect" ? `${factor.data}-rect` : factor.data;

        if (factor.data != "cumulativeGDU") {
          const url = this.getFactorRequestUrl(
            factor.data,
            factor.mark,
            factor.data === "hsu" ? this.getHsuCropParam() : cropSelected,
            coords,
            startDate,
            endDate,
            urlAppend
          );
          const promise = this.StrategyCommonService.makeClimaticDataRequest(
            this.HsUtilsService.proxify(url),
            factorName,
            this.fetchedData
          );
          observables.push(promise);
        }
      }
    }

    forkJoin(observables).subscribe((data: Array<any>) => {
      if (data.every((observable) => observable.error)) {
        this.StrategyCommonService.handleFetchError(data);
      } else {
        this.modifyInputData(dateRange);
        this.StrategyCommonService.baseChartsLoading = false;
      }
    });
  }

  /**
   * Create API URL for requested factor
   */
  private getFactorRequestUrl(
    factor,
    mark,
    cropSelected,
    coords,
    startDate,
    endDate,
    urlAppend
  ) {
    return [
      this.StrategyCommonService.apiUrl,
      this.dataFrequency,
      factor,
      cropSelected,
      coords[1],
      coords[0],
      "0.1",
      mark == "rect" ? (startDate - 5).toString() : startDate.toString(),
      mark == "rect" ? (startDate + 5).toString() : endDate.toString(),
      urlAppend,
    ].join("/");
  }

  /**
   * Prepare date interval object used in vega
   * Standalone object per year/week
   */
  createDateRangeObject(dataFrequency, startDate, endDate) {
    return this.StrategyCommonService.createDateRangeObject(
      dataFrequency,
      startDate,
      endDate
    );
  }

  /**
   * Modify fetched data
   * - asigning values to created dateRange object
   * - other modifications (gdu stacks etc.)
   */
  modifyInputData(dateRange): void {
    if (this.dataFrequency == "week") {
      this.StrategyCommonService.getFrostFreeSeason(this.fetchedData);
    }
    //For each year/week..
    for (const [index, date] of dateRange.entries()) {
      let keyModifier = this.dataFrequency == "week" ? `_${date.week}` : "";
      this.parserService.parseFetchedDataValues(
        this.fetchedData,
        date,
        keyModifier
      );
      //Modify year or week value to 'YYYY-MM-DD' format
      if (this.dataFrequency == "week") {
        this.StrategyCommonService.isWithinFrostPeriod(date);
        date.week = `${this.parserService.getDateOfWeek(date.week, date.year)}`;
      } else {
        date.year = `${date.year}-01-01`;
      }
      if (this.hasFactor("cumulativeGDU") && index >= 3) {
        const cumulativeGDUObject = this.calculateCumulativeGDU(
          dateRange,
          date,
          index
        );
        date.cumulativeGDU = cumulativeGDUObject.fac;
        date.cumulativeGDUup = cumulativeGDUObject.up;
        date.cumulativeGDUdwn = cumulativeGDUObject.dwn;
      }
    }

    if (
      this.hasFactor("gdu") &&
      this.StrategyCommonService.overview == "crop"
    ) {
      const callback = `calculate${this.StrategyCommonService.dataFrequency}lyGduStacks`;
      dateRange = this[callback](dateRange, "gdu");
    }

    if (
      this.hasFactor("preci") &&
      this.StrategyCommonService.overview == "crop" &&
      this.dataFrequency == "week"
    ) {
      let maturityFound = false;
      let emergenceFound = false;

      dateRange = this.calculateweeklyGduStacks(dateRange, "preci");
      //Add preciCategory,Legend values to object which were created by calculating GDU stacks
      for (const [index, week] of dateRange.slice(53).entries()) {
        const fetchedWeeks = dateRange.slice(0, 53);
        for (const fetchedWeek of fetchedWeeks) {
          if (fetchedWeek.week == week.week && !week.preciCategory) {
            //Merge
            week.preciCategory = fetchedWeek.preciCategory;
            week.preciLegend = fetchedWeek.preciLegend;
          }
        }
      }
    }
    dateRange = dateRange.sort((a, b) => a.gduCategory - b.gduCategory);

    if (this.dataFrequency == "week") {
      //Remove first week value because it usually falls into previous year and is
      //rendered as last-but-ones value in chart which is causing inconsistencies
      //dateRange.shift();
    }

    this.datasets = {
      //CHANGE NAME OF WEEK_DATA PROPERTY!!!!
      week_data: dateRange,
      rect_data: this.getRectMarkData(),
    };
  }

  /**
   * Split dateRange objects which include multiple crop phenophases based on model break values.
   * Assign phenophase to each week
   */
  calculateweeklyGduStacks(dateRange, factor: "gdu" | "preci") {
    const stackArray = [];
    const seasonStart: number = this.parserService.getSeasonStartWeekNumber(
      this.StrategyCommonService.seasonStartDate
    );
    let cropModel =
      this.cropModels[this.StrategyCommonService.selectedCrop][factor];
    cropModel = factor == "gdu" ? cropModel[this.dataFrequency] : cropModel;
    const legendObject =
      factor == "gdu"
        ? this.cropLegend[this.StrategyCommonService.selectedCrop]
        : this.preciLegends;

    const FACTOR_UP = `${factor}up`; //eg. gduup
    const FACTOR_DWN = `${factor}dwn`;
    const FACTOR_CATEGORY = `${factor}Category`;
    const FACTOR_LEGEND = `${factor}Legend`;

    let phenoPhase = 1; //Starting with 1 because 0 represents dates before season begins
    let cumulativeCount = 0;
    for (const [index, week] of dateRange.slice(0, 53).entries()) {
      //4 = constat for season start padding
      week[factor] = index < 4 && factor == "gdu" ? 0 : week[factor];
      week[FACTOR_UP] = index < 4 && factor == "gdu" ? null : week[FACTOR_UP];
      week[FACTOR_DWN] = index < 4 && factor == "gdu" ? null : week[FACTOR_DWN];

      week[FACTOR_CATEGORY] = 0;
      //&& index <= this.StrategyCommonService.seasonEndWeek
      //ukončit napočítavanie hodnot po prvom mraze
      if (index >= 4) {
        const assignPhenoPhase = () => {
          return factor == "gdu"
            ? phenoPhase
            : week.gduCategory ==
              this.cropModels[this.StrategyCommonService.selectedCrop].gdu[
                this.dataFrequency
              ].length
            ? 0
            : phenoPhase;
        };

        //Store factor value in order to calculate wether the breakpoint was reached or not
        const factorValue = week[factor];
        cumulativeCount += factorValue;
        //Increase cumulative count until pheno phase breakpoint
        if (
          cumulativeCount < cropModel[phenoPhase] ||
          phenoPhase == cropModel.length
        ) {
          week[FACTOR_CATEGORY] = assignPhenoPhase();
        } else {
          week[FACTOR_CATEGORY] = assignPhenoPhase();
          week[factor] =
            factorValue - (cumulativeCount - cropModel[phenoPhase]);

          cumulativeCount = cumulativeCount - cropModel[phenoPhase];
          phenoPhase += 1;

          //Creates stack object assigning original object new values
          const createStackObject = (value) => {
            return Object.assign(Object.assign({}, week), {
              week: week.week,
              [FACTOR_CATEGORY]: assignPhenoPhase(),
              [factor]: value,
              [FACTOR_LEGEND]:
                this.hsLangService.getTranslationIgnoreNonExisting(
                  `CHARTS.week${FACTOR_LEGEND}`,
                  legendObject[phenoPhase]
                ),
              frost: false, //Dont want overlaping bars
            });
          };
          //Splits week value into 2. Assigning new value to original object
          //and creates new one with the difference
          const splitValueIfNecessary = () => {
            if (cumulativeCount > cropModel[phenoPhase]) {
              cumulativeCount = cumulativeCount - cropModel[phenoPhase];
              const stack = createStackObject(cropModel[phenoPhase]);
              phenoPhase += 1;
              stackArray.push(stack);
              splitValueIfNecessary();
            } else {
              const stack = createStackObject(cumulativeCount);
              if (factor === "gdu") {
                this.findGrowingStage(stack, 2);
                this.findGrowingStage(
                  stack,
                  this.cropModels[this.StrategyCommonService.selectedCrop].gdu[
                    this.dataFrequency
                  ].length
                );
              }
              stackArray.push(stack);
            }
          };
          splitValueIfNecessary();
        }
      }
      week[FACTOR_LEGEND] = this.hsLangService.getTranslationIgnoreNonExisting(
        `CHARTS.week${FACTOR_LEGEND}`,
        legendObject[week[FACTOR_CATEGORY]]
      );
    }
    return [...dateRange, ...stackArray];
  }

  /**
   * Looks for week when emregence/maturity of crop begins
   * Marks corresponding week object with properties used in vega
   */
  findGrowingStage(week, stagePosition) {
    if (week.gduCategory == stagePosition) {
      week.stageBegining = week.week;
      week.stageCategory = week.gduCategory;
    }
  }

  /**
   * Modifies dateRange object by multiply number of occurences of each date object in order for each object to hold
   * one gdu stack(category) identifier and value
   */
  calculateyearlyGduStacks(dateRange) {
    const stackArray = [];
    for (let entry of dateRange) {
      const gdu = entry.gdu; //2800
      //TODO: dynamic based on specific crop model
      const cropModelBreakpoints =
        this.cropModels[this.StrategyCommonService.selectedCrop].gdu.year;
      //Assing entry a gdu category
      entry.gduCategory = 0;
      entry.gduLegend = this.hsLangService.getTranslationIgnoreNonExisting(
        "CHARTS.yearGduLegend",
        this.cropLegend.gduAmount[0]
      );
      //GDU exceeded first breakpoint
      if (gdu - cropModelBreakpoints[0] > 0) {
        entry.gdu = cropModelBreakpoints[0];
        //GDU exceeded second breakpoint
        if (
          gdu - cropModelBreakpoints[0] >
          cropModelBreakpoints[1] - cropModelBreakpoints[0]
        ) {
          //Use range between first and second breakpoint as GDU value
          stackArray.push({
            year: entry.year,
            gduCategory: 1,
            gdu: cropModelBreakpoints[1] - cropModelBreakpoints[0],
            gduLegend: this.hsLangService.getTranslationIgnoreNonExisting(
              "CHARTS.yearGduLegend",
              this.cropLegend.gduAmount[1]
            ),
          });
          //Use rest of gdu
          stackArray.push({
            year: entry.year,
            gduCategory: 2,
            gdu: gdu - cropModelBreakpoints[1],
            gduLegend: this.hsLangService.getTranslationIgnoreNonExisting(
              "CHARTS.yearGduLegend",
              this.cropLegend.gduAmount[2]
            ),
          });
        } else {
          stackArray.push({
            year: entry.year,
            gduCategory: 1,
            gdu: gdu - cropModelBreakpoints[0],
            gduLegend: this.hsLangService.getTranslationIgnoreNonExisting(
              "CHARTS.yearGduLegend",
              this.cropLegend.gduAmount[1]
            ),
          });
        }
      }
    }
    return [...stackArray, ...dateRange];
  }

  /**
   * Determines whether there is a 'factor' among parsed data
   */
  private hasFactor(factor) {
    return this.StrategyCommonService.selectedDataPairs[
      this.StrategyCommonService.overview
    ][this.dataFrequency].some((item: any) => {
      return Array.isArray(item.data)
        ? item.data.includes(factor)
        : item.data == factor;
    });
  }

  private calculateCumulativeGDU(dateRange, date, index) {
    return {
      fac: this.accumulateFactor(date.gdu, dateRange[index - 1].cumulativeGDU),
      up: this.accumulateFactor(
        date.gduup,
        dateRange[index - 1].cumulativeGDUup
      ),
      dwn: this.accumulateFactor(
        date.gdudwn,
        dateRange[index - 1].cumulativeGDUdwn
      ),
    };
  }

  private accumulateFactor(value, prevValue) {
    return prevValue >= 0 ? value + prevValue : 0;
  }

  strategyChartOverrides(
    options: any,
    chartTemplate,
    dimensions,
    langService
  ): any {
    const baseChart =
      options.dataFrequency == "week"
        ? chartTemplate.layer[1]
        : chartTemplate.layer[0];

    const generatePhenoPhaseIndicators = (chartTemplate, baseChart) => {
      chartTemplate.layer.push(JSON.parse(JSON.stringify(baseChart)));
      chartTemplate.layer[chartTemplate.layer.length - 1].encoding = {
        x: {
          field: "stageBegining",
          timeUnit: "yearweek",
          type: "ordinal",
        },
        color: { value: "#a1a1a1a8" },
        size: { value: 2 },
        tooltip: { field: "gduLegend" },
      };
      chartTemplate.layer[chartTemplate.layer.length - 1].transform = [
        { filter: "datum.stageBegining" },
      ];
      chartTemplate.layer[chartTemplate.layer.length - 1].mark = "rule";
      delete chartTemplate.layer[chartTemplate.layer.length - 1].selection;
    };

    if (
      (options.mark === "line" || options.mark.includes("line")) &&
      !options.yAxis.includes("cumulative")
    ) {
      Object.assign(baseChart.mark, {
        interpolate: "step",
        point: {
          filled: false,
          fill: "white",
        },
      });
    }
    /**
     * fertdate
     */
    if (options.yAxis === "fertdate") {
      const index = options.uncertainity ? 2 : 1;
      // MODIFY DECLARATIONS FOR THIS SPECIFIC CHART
      // DATA LAYER
      delete baseChart.encoding.y.type;
      delete baseChart.encoding.y.scale;
      baseChart.encoding.y.stack = <any>true;
      baseChart.encoding.tooltip[1].type = "temporal";
      baseChart.encoding.tooltip[1].format = "%d. %B";
      Object.assign(baseChart.encoding.y, {
        timeUnit: "utcmonthdate",
        sort: "descending",
      });
      Object.assign(baseChart.mark, {
        interpolate: "step",
        point: true,
        line: true,
      });
      // OVERLAY LAYER
      delete chartTemplate.layer[index].encoding.x;
      delete chartTemplate.layer[index].encoding.y.type;
      chartTemplate.layer[index].mark.type = "rule";
      chartTemplate.layer[index].transform = <any>[
        {
          timeUnit: "dayofyear",
          field: "fertdate",
          as: "dayinyeartemp",
        },
        {
          joinaggregate: [
            { op: "mean", field: "dayinyeartemp", as: "meanDay" },
          ],
        },
      ];

      Object.assign(chartTemplate.layer[index].encoding.y, {
        timeUnit: "dayofyear",
        field: "meanDay",
      });

      if (index === 2) {
        chartTemplate.layer[1].mark = {
          type: "bar",
          tooltip: true,
          interpolate: "step",
          stroke: "red",
          fillOpacity: 0.8,
          color: "red",
          width: { band: 0.25 },
        };
        chartTemplate.layer[1].encoding = JSON.parse(
          JSON.stringify(baseChart.encoding)
        );
        delete chartTemplate.layer[1].encoding.color;
        chartTemplate.layer[1].encoding.y.field = "fertdatedwn";
        chartTemplate.layer[1].encoding.y2 = Object.assign(
          {},
          baseChart.encoding.y
        );
        chartTemplate.layer[1].encoding.y2.field = "fertdateup";
      }
    }
    /**
     * GDU - BAR
     */
    if (options.yAxis === "gdu" && options.mark == "bar") {
      let range =
        options.dataFrequency == "week"
          ? { scheme: "tableau10" }
          : { range: ["#f3092b", "#f9e400", "#289428c7"] };
      baseChart.encoding.y = {
        aggregate: "sum",
        field: "gdu",
        axis: baseChart.encoding.y.axis,
      };
      Object.assign(baseChart.encoding.color, {
        field: "gduLegend",
        scale: range,
        legend: {
          labelLimit: 50,
          title: null,
        },
        sort: "gduCategory",
      });
      delete baseChart.encoding.color.value;
      baseChart.encoding.order = { field: "gduCategory" };
      baseChart.encoding.tooltip.push({
        field: "gduLegend",
        title: langService.getTranslationIgnoreNonExisting(
          "CHARTS",
          "growingPhase"
        ),
      });
    }
    /**
     * WEEK - GDU - BAR - CROP
     */
    if (
      options.yAxis === "preci" &&
      options.mark == "bar" &&
      options.overview == "crop" &&
      options.dataFrequency == "week"
    ) {
      baseChart.encoding.y = {
        aggregate: "sum",
        field: "preci",
        axis: baseChart.encoding.y.axis,
      };
      Object.assign(baseChart.encoding.color, {
        field: "preciLegend",
        scale: { range: ["#b5b5b5", "#f3092b", "#f9e400", "#289428c7"] },
        legend: {
          labelLimit: 50,
          title: null,
        },
        sort: "preciCategory",
      });
      delete baseChart.encoding.color.value;
      baseChart.encoding.order = { field: "preciCategory" };

      generatePhenoPhaseIndicators(chartTemplate, baseChart);
      baseChart.encoding.tooltip.push({
        field: "preciLegend",
        title: langService.getTranslationIgnoreNonExisting(
          "COMMON",
          "category"
        ),
      });
    }
    if (options.yAxis === "wbalanc") {
      baseChart.encoding.y.scale.zero = true;
    }
    if (options.yAxis === "cumulativeGDU") {
      generatePhenoPhaseIndicators(chartTemplate, baseChart);
      if (options.uncertainity) {
        chartTemplate.layer[3].mark.type = "errorband";
      }
    }
  }

  getDefinitions() {
    const dataPairs = this.StrategyCommonService.parseSelectedDataPairs();
    return {
      rect: this.getRectDefinition("wbalanc"),
      charts: dataPairs[this.StrategyCommonService.overview],
    };
  }

  getRectMarkData() {
    const factorForRecatangle = this.StrategyCommonService.selectedDataPairs[
      this.StrategyCommonService.overview
    ][this.dataFrequency].filter((item) => item.mark == "rect")[0];
    if (!factorForRecatangle) {
      return null;
    }

    let rectData = this.fetchedData[`${factorForRecatangle.data}-rect`];

    return Object.entries(rectData)
      .filter(([key, val]) => key.includes("fac"))
      .map(([key, val]) => {
        let keyParts = key.split("_");
        let year = parseInt(keyParts[0].slice(-4));
        let week = keyParts[1];
        return {
          year: year,
          week: `${this.parserService.getDateOfWeek(parseInt(week), year)}`,
          [factorForRecatangle["data"]]: parseFloat(val as string),
        };
      });
  }

  getRectDefinition(factor) {
    return {
      data: {
        name: "rect_data",
      },
      title: {
        text: this.hsLangService.getTranslationIgnoreNonExisting(
          "CHARTS.title",
          "TEST water balance will be available from API"
        ),
      },
      // width: this.HsLayoutService.panelSpaceWidth() * 0.8,
      height: this.HsLayoutService.panelSpaceWidth() * 0.17,
      selection: {
        my_selection: {
          type: "interval",
          empty: "none",
          mark: { stroke: "grey" },
        },
      },
      encoding: {
        x: {
          axis: {
            title: "Week",
          },
          field: "week",
          type: "ordinal",
          timeUnit: "week",
        },
        y: {
          field: "week",
          type: "ordinal",
          timeUnit: "year",
        },
        color: {
          field: factor,
          type: "quantitative",
          // https://vega.github.io/vega/docs/schemes/
          scale: { scheme: "blueorange" },
          legend: {
            title: null,
            orient: "top",
            type: "symbol",
            symbol: {
              symbolSize: 150,
            },
          },
        },
        stroke: {
          condition: {
            selection: "my_selection",
            value: "red",
          },
          value: null,
        },
      },
      mark: { type: "rect", tooltip: true },
      name: "agroInfoChart-rect",
    };
  }
}
